import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, Heart, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { WelcomeSettingsDialog } from './WelcomeSettingsDialog';
import { clientWelcomeAPI } from '../lib/api';

const PAGOS = [['sepa', 'SEPA'], ['transferencia', 'Transferencia']];

const StepToggle = ({ done, label, busy, onClick, testId }) => (
  <button type="button" onClick={onClick} disabled={busy} data-testid={testId}
    className="w-full text-left text-sm flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted transition-colors">
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
    <span className={done ? '' : 'text-muted-foreground'}>{label}</span>
  </button>
);

export default function ClientWelcomeSetup({ clientId }) {
  const [data, setData] = useState(null);
  const [discord, setDiscord] = useState('');
  const [forma, setForma] = useState('');
  const [saving, setSaving] = useState(false);
  const [stepBusy, setStepBusy] = useState(null);

  const toggle = async (stepId, hecho) => {
    setStepBusy(stepId);
    try { setData(await clientWelcomeAPI.setClientStep(clientId, stepId, hecho)); }
    catch (e) { toast.error(e.message); } finally { setStepBusy(null); }
  };

  const load = useCallback(() => clientWelcomeAPI.getClient(clientId).then((d) => {
    setData(d); setDiscord(d.discord_invite_url); setForma(d.forma_pago);
  }).catch((e) => toast.error(e.message)), [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const save = async () => {
    setSaving(true);
    try { setData(await clientWelcomeAPI.saveClient(clientId, { discord_invite_url: discord, forma_pago: forma })); toast.success('Datos de bienvenida guardados'); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <Card data-testid="client-welcome-setup">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-primary" />Bienvenida del portal</CardTitle>
        <WelcomeSettingsDialog onSaved={load} />
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">Rellena estos datos antes de invitar al cliente: los verá en su pestaña de Bienvenida al entrar.</p>
        <div className="space-y-1.5">
          <Label>Enlace de invitación a su canal de Discord</Label>
          <Input value={discord} onChange={(e) => setDiscord(e.target.value)} placeholder="https://discord.gg/..." data-testid="welcome-discord-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Forma de pago</Label>
          <div className="flex gap-2">
            {PAGOS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForma(forma === v ? '' : v)} data-testid={`welcome-pago-${v}`}
                className={`px-4 h-9 rounded-full border text-sm transition-colors ${forma === v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>{l}</button>
            ))}
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="welcome-client-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar</Button>
        <div className="space-y-2 border-t pt-4" data-testid="welcome-client-progress">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-medium">Progreso del cliente · {data.completed_count} de {data.total_count}</p>
            {!data.all_done && (
              <Button size="sm" variant="outline" disabled={!!stepBusy} onClick={() => toggle('all', true)} data-testid="welcome-mark-all-btn">
                Marcar todo como hecho
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Pulsa un paso para marcarlo o desmarcarlo por el cliente (por ejemplo, si ya estaba incorporado). La forma de pago se marca eligiéndola arriba y guardando.</p>
          {data.discord_invite_url && (
            <StepToggle done={data.discord_joined} label="Tu canal de Discord" busy={stepBusy === 'discord'} onClick={() => toggle('discord', !data.discord_joined)} testId="welcome-client-progress-discord" />
          )}
          <p className="text-sm flex items-center gap-2 px-2 py-1" data-testid="welcome-client-progress-pago">
            {data.forma_pago ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
            Forma de pago{data.forma_pago ? ` · ${data.forma_pago === 'sepa' ? 'SEPA' : 'Transferencia'}${data.forma_pago_by_client ? ' (elegida por el cliente)' : ''}` : ''}
          </p>
          {data.pasos.map((p) => (
            <StepToggle key={p.id} done={data.completed_steps.includes(p.id)} label={p.titulo} busy={stepBusy === p.id}
              onClick={() => toggle(p.id, !data.completed_steps.includes(p.id))} testId={`welcome-client-step-${p.id}`} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
